'use client';

import type { ReactNode } from 'react';

import './ProductDetails.css';

export type ProductDetailsLayoutProps = {
  isMobileDevice: boolean;
  /** Row above the grid, e.g. `ProductDetailsTopBar` (back link + favourites). */
  topBar?: ReactNode;
  /** Hero media in the left column: image gallery, 3D viewer, … */
  media: ReactNode;
  /**
   * Title block. Sits above the media on mobile and at the top of the right
   * column on desktop, matching the reading order on each viewport.
   */
  header?: ReactNode;
  /** Right column under the header: description, swatches, pricing, CTAs. */
  aside?: ReactNode;
  /** Dealer-authored HTML from the `PDPContentTopSection` token. */
  topSectionContent?: ReactNode;
  /** Middle sections: visualizer, configurator, specs, similar products… */
  children?: ReactNode;
  /** Bottom section, e.g. `ProductDetailsRequestForm`. */
  footer?: ReactNode;
};

/**
 * Page shell shared by every product detail page (flooring, cabinetry, …).
 * It owns only the three-band structure and the responsive grid; what goes in
 * each slot is decided by the composing page so the same chrome serves
 * catalogs with very different content.
 */
export const ProductDetailsLayout = ({
  isMobileDevice,
  topBar,
  media,
  header,
  aside,
  topSectionContent,
  children,
  footer,
}: ProductDetailsLayoutProps) => (
  <div>
    <section className="pdp__top-section ui-py-8 ui-pb-12 ui-bg-gray-100">
      <div className="ui-container !ui-max-w-screen-xl">
        {topBar && (
          <div className="pdp__backlink ui-mb-6 ui-flex ui-items-center ui-justify-between">
            {topBar}
          </div>
        )}

        <div className="pdp__top-section-grid ui-min-0 ui-grid md:ui-grid-cols-2 lg:ui-grid-cols-5 ui-gap-6 md:ui-gap-12">
          <div className="pdp__top-section-col-1 lg:ui-col-span-2">
            {isMobileDevice && header}
            <div className="pdp__gallery">{media}</div>
          </div>

          <div className="pdp__top-section-col-2 ui-min-w-0 lg:ui-col-span-3 md:ui-mt-2 ui-space-y-3">
            {!isMobileDevice && header}
            {aside}
          </div>

          {topSectionContent && (
            <div
              className="pdp__top-section-col-3"
              data-token-generated="PDPContentTopSection"
            >
              {topSectionContent}
            </div>
          )}
        </div>
      </div>
    </section>

    <section className="pdp__middle-section">
      <div className="ui-container !ui-max-w-screen-xl ui-space-y-8 lg:ui-space-y-12 ui-py-8 md:ui-py-12">
        {children}
      </div>
    </section>

    {footer && (
      <section className="pdp__bottom-section ui-bg-gray-100 ui-py-8 md:ui-py-12">
        <div className="ui-container !ui-max-w-screen-xl">{footer}</div>
      </section>
    )}
  </div>
);
